/**
 * In-app browser detection utilities.
 *
 * Detects embedded browsers from apps like LINE, Facebook, Instagram, etc.
 * Google OAuth does not work in these browsers due to security restrictions.
 *
 * @module @f2e/shared/browser/in-app-browser
 */
/** Patterns for known in-app browsers (excludes "unknown" which is handled separately) */
const BROWSER_PATTERNS = {
    line: /\bLine\//i,
    facebook: /\bFB[A-Z]|FBAN|FBAV|FB_IAB/i,
    instagram: /\bInstagram/i,
    wechat: /\bMicroMessenger/i,
    tiktok: /\bBytedance|TikTok/i,
    twitter: /\bTwitter/i,
    telegram: /\bTelegram/i,
    snapchat: /\bSnapchat/i,
    pinterest: /\bPinterest/i,
    linkedin: /\bLinkedIn/i,
};
/**
 * Checks if the User-Agent indicates an Android WebView
 */
function isAndroidWebView(ua) {
    return /; wv\)/.test(ua);
}
/**
 * Checks if the User-Agent indicates an iOS WebView (WKWebView/UIWebView)
 * iOS WebViews typically have AppleWebKit but no Safari in the UA
 */
function isIOSWebView(ua) {
    return /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(ua);
}
/**
 * Checks if the browser is a generic WebView (not matching specific apps)
 */
function isGenericWebView(ua) {
    return isAndroidWebView(ua) || isIOSWebView(ua);
}
/**
 * Checks if the User-Agent indicates an Android device
 */
function isAndroid(ua) {
    return /Android/i.test(ua);
}
/**
 * Determines if the specific browser supports opening external browser
 * Takes platform into account since some methods only work on specific platforms
 */
function canOpenExternal(browser, ua) {
    switch (browser) {
        case "line":
            // LINE supports openExternalBrowser=1 parameter on all platforms
            return true;
        case "facebook":
        case "instagram":
            // Meta apps only support intent URLs on Android
            return isAndroid(ua);
        default:
            return false;
    }
}
/**
 * Detects if the current browser is an in-app browser
 *
 * @param userAgent - Optional User-Agent string (defaults to navigator.userAgent)
 * @returns Information about the detected in-app browser
 */
export function detectInAppBrowser(userAgent) {
    const ua = userAgent ?? (typeof navigator !== "undefined" ? navigator.userAgent : "");
    // Check specific app patterns first
    for (const [name, pattern] of Object.entries(BROWSER_PATTERNS)) {
        if (pattern.test(ua)) {
            return {
                isInAppBrowser: true,
                browserName: name,
                canOpenExternalBrowser: canOpenExternal(name, ua),
            };
        }
    }
    // Check for generic WebView indicators
    if (isGenericWebView(ua)) {
        return {
            isInAppBrowser: true,
            browserName: "unknown",
            canOpenExternalBrowser: false,
        };
    }
    return {
        isInAppBrowser: false,
        browserName: null,
        canOpenExternalBrowser: true,
    };
}
/**
 * Attempts to open the current URL in an external browser
 *
 * Different in-app browsers have different mechanisms:
 * - LINE: Append openExternalBrowser=1 parameter
 * - Facebook/Instagram on Android: Use intent URL
 *
 * @param url - URL to open (defaults to current URL)
 * @returns true if an attempt was made, false otherwise
 */
export function tryOpenExternalBrowser(url) {
    if (typeof window === "undefined")
        return false;
    const currentUrl = url ?? window.location.href;
    const info = detectInAppBrowser();
    if (!info.isInAppBrowser)
        return false;
    switch (info.browserName) {
        case "line": {
            // LINE supports openExternalBrowser=1 parameter
            const lineUrl = new URL(currentUrl);
            lineUrl.searchParams.set("openExternalBrowser", "1");
            window.location.href = lineUrl.toString();
            return true;
        }
        case "facebook":
        case "instagram": {
            // Meta apps on Android support intent URLs
            if (/Android/i.test(navigator.userAgent)) {
                const intentUrl = `intent://${currentUrl.replace(/^https?:\/\//, "")}#Intent;scheme=https;end`;
                window.location.href = intentUrl;
                return true;
            }
            return false;
        }
        default:
            return false;
    }
}
